import { type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { DT } from '../../theme/designTokens';
import { AppShellProvider, useAppShell } from '../../context/AppShellContext';
import { useAuth } from '../../context/AuthContext';
import { useIdentity, resolveDisplayName } from '../../hooks/useIdentity';
import { useAccountLanguageSync } from '../../hooks/accountLanguage';
import { usePushStartupMaintenance } from '../../hooks/pushMaintenance';
import { TopNav } from './TopNav';
import { Sidebar } from './Sidebar';
import { Drawer } from './Drawer';
import { GameInvitationToast } from '../social/GameInvitationToast';
import { SocialToastManager } from '../social/SocialToastManager';
import { ActiveGameBanner } from './ActiveGameBanner';
import { OfflineBanner } from './OfflineBanner';
import { AppTour } from '../tutorial/AppTour';
import { ChatDockProvider } from '../../context/ChatDockContext';
import { ChatDockLayer } from '../chat/dm/ChatDockLayer';

interface Props {
  children?: ReactNode;
}

/**
 * DS2 migration bridge — routes in this list use the new persistent Sidebar
 * (tablet/desktop) instead of the legacy TopNav. Mobile is unaffected either
 * way; it always uses TopNav's hamburger + Drawer.
 *
 * This is intentionally a single, explicit, shrinking list rather than a
 * parallel shell system: every other route keeps rendering through the
 * original TopNav path below, completely unchanged. As screens migrate to
 * DS2 they get added here. The end goal is for this list to cover every
 * route, at which point TopNav's desktop layout (not its mobile hamburger
 * bar, which stays) can be retired and AppShell simplified to always use
 * Sidebar — a single unified shell, not two permanent ones.
 */
const SIDEBAR_SHELL_ROUTES = ['/', '/game', '/spectate', '/lobby', '/forgot-password', '/messages'];

/**
 * Exact-match variant of the bridge list: the route itself uses the DS2
 * shell but its children do NOT. '/signup' is the Get Started chooser;
 * '/signup/email' and '/signup/username' are the 2-step email-registration
 * flow (Figma "Register with email 1/2") — all DS2, same shell as Login.
 */
const SIDEBAR_SHELL_ROUTES_EXACT = ['/signup', '/signup/email', '/signup/username', '/login', '/home', '/leaderboard', '/friends', '/play/ai', '/play/online', '/settings', '/rules', '/achievements'];

function usesSidebarShell(pathname: string): boolean {
  return (
    SIDEBAR_SHELL_ROUTES.some(p => pathname === p || pathname.startsWith(p + '/')) ||
    SIDEBAR_SHELL_ROUTES_EXACT.includes(pathname)
  );
}

function AppShellInner({ children }: Props) {
  const { drawerOpen, closeDrawer } = useAppShell();
  const { pathname } = useLocation();
  const { user, isLoggedIn, devDisplayName } = useAuth();
  const identity = useIdentity();
  const sidebarShell = usesSidebarShell(pathname);

  // Account language preference (Localization Phase 10): apply the account's
  // stored locale once per identity, and bootstrap a null account from the
  // device-resolved language — its own dedicated effect/seam, deliberately
  // independent of the push effect below.
  useAccountLanguageSync();

  // Push is explicit opt-in (Phase 11): the ONLY place a permission prompt
  // may originate is the Settings toggle gesture. This hook performs SILENT
  // registration maintenance for already-opted-in users on already-granted
  // devices — it is structurally unable to prompt (see hooks/pushMaintenance).
  usePushStartupMaintenance();

  // Username-first: backend display_name → Firebase displayName → email prefix
  const sidebarUser = isLoggedIn
    ? {
        displayName: resolveDisplayName(identity, user, devDisplayName || 'Player'),
        avatarKey:   identity?.avatarKey ?? null,
        avatarUrl:   identity?.avatarUrl ?? undefined,
      }
    : null;

  return (
    <div
      style={{
        position:   'fixed',
        inset:      0,
        width:      '100vw',
        height:     '100dvh',
        background: DT.color.pageBg,
        overflow:   'hidden',
        display:    sidebarShell ? 'flex' : undefined,
        filter:     identity?.hdrEnabled ? 'contrast(1.1) saturate(1.2) brightness(1.03)' : undefined,
        transition: 'filter 300ms ease',
      }}
    >
      {sidebarShell ? (
        <>
          {/* Tablet/desktop: persistent Sidebar (this route has migrated to DS2) */}
          <div className="hidden md:flex" style={{ height: '100%' }}>
            <div className="flex lg:hidden" style={{ height: '100%' }}>
              <Sidebar variant="collapsed" user={sidebarUser} />
            </div>
            <div className="hidden lg:flex" style={{ height: '100%' }}>
              <Sidebar variant="full" user={sidebarUser} />
            </div>
          </div>
          {/* Mobile: TopNav hamburger bar in DS2 chrome — Drawer remains the mobile nav pattern */}
          <div className="md:hidden">
            <TopNav ds2 />
          </div>
        </>
      ) : (
        <TopNav />
      )}

      <Drawer open={drawerOpen} onClose={closeDrawer} ds2={sidebarShell} />
      {/* Guided onboarding — Chapter 1 (app tour). Inert unless activated at
          /home?tour=app while logged in; needs AppShellContext (drawer). */}
      <AppTour />
      <GameInvitationToast />
      <SocialToastManager />
      {/* Docked Chat Redesign: shell-level DM dock (desktop only). Rendered
          here — not inside the Sidebar or any page — so route navigation
          re-renders but never remounts open windows, minimized tabs or
          drafts. Self-hides (CSS, state preserved) on game/spectate/tutorial
          routes via the useDmPresentationSuppressed seam. */}
      <ChatDockLayer />
      {/* Global active-game surface: every authenticated screen EXCEPT the game
          itself. Solo → "Bot game saved"; multiplayer → urgent "Match in progress". */}
      {isLoggedIn && !pathname.startsWith('/game') && <ActiveGameBanner />}

      {/* Global connectivity surface: every screen EXCEPT the live game/spectate
          view, which already has its own ActionCable reconnect UX (see
          OfflineBanner.tsx). Not gated on isLoggedIn — a dropped connection
          matters on the login/landing screens too. */}
      {!pathname.startsWith('/game') && !pathname.startsWith('/spectate') && <OfflineBanner />}

      {/* Scrollable content area. overscrollBehavior:'contain' on both variants
          stops scroll-chaining/rubber-band bounce from bleeding past this
          container's own bottom edge into the fixed shell behind it. */}
      {sidebarShell ? (
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden" style={{ display: 'flex', flexDirection: 'column', overscrollBehavior: 'contain', paddingBottom: DT.layout.safeAreaBottom }}>
          {/* Mobile: fixed TopNav needs an in-flow spacer (pt-16 is not in the
              generated Tailwind CSS, so a padding class silently does nothing).
              Matches TopNav's own height exactly, including its status-bar/
              notch inset (topNavHeightCss) — otherwise native content starts
              underneath the (now taller) bar. */}
          <div className="md:hidden" style={{ height: DT.layout.topNavHeightCss, flexShrink: 0 }} aria-hidden />
          {/* flex:1;minHeight:0 makes THIS wrapper fill main's available height —
              but a plain child of a column-flex parent does NOT auto-stretch to
              fill that height (only its width stretches); each screen's own root
              div must repeat flex:1;minHeight:0;display:flex;flexDirection:column
              one level further for its Footer's marginTop:'auto' to actually
              reach the bottom instead of leaving blank shell background below a
              short page (see LandingScreen/HomeDS2Screen/LeaderboardScreen/
              FriendsDS2Screen/PlayOnlineScreen root divs + Footer.tsx). */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {children ?? <Outlet />}
          </div>
        </main>
      ) : (
        <main
          style={{
            position:  'absolute',
            // Legacy shell's TopNav is never taller than topNavHeight (ds2
            // prop always false here), but it still sits under the status
            // bar on native, so the content offset needs the same inset.
            top:       DT.layout.topNavHeightCss,
            left:      0,
            right:     0,
            bottom:    0,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            paddingBottom: DT.layout.safeAreaBottom,
          }}
        >
          {children ?? <Outlet />}
        </main>
      )}
    </div>
  );
}

export function AppShell({ children }: Props) {
  return (
    <AppShellProvider>
      {/* ChatDockProvider is presentation-only state for the DM dock (open
          windows, focus order, drafts, suppression). It sits below
          FriendProvider (its data authority) and above both the Sidebar
          (Chat module) and the dock layer. */}
      <ChatDockProvider>
        <AppShellInner>{children}</AppShellInner>
      </ChatDockProvider>
    </AppShellProvider>
  );
}
